/**
 * Role helpers (F045 — RBAC for persona lenses)
 *
 * Roles come exclusively from the Entra access token's `roles` claim (see
 * `AuthContext.tsx` — never a database role store, per `.claude/rules/app-registration-credentials.md`).
 * Comparisons here are case-insensitive because the app registration's `appRoles[].value`
 * casing has drifted across roles added at different times (`System.Admin` vs a hypothetical
 * `system.admin`) — matching case-sensitively is a latent bug, not a feature.
 *
 * Gate affordances by HIDING them (`{can && ...}`), never by disabling — a disabled control
 * still leaks that the capability exists to a user who cannot reach it.
 */

const STAFF_ROLES = ['System.Admin', 'Organization.Admin'];
const ADMIN_ROLES = ['TenantAdmin', 'Admin', 'Organization.Admin', 'System.Admin'];

const normalise = (role: string) => role.trim().toLowerCase();

/** True if `userRoles` contains at least one of `required`, compared case-insensitively. */
export function hasAnyRole(userRoles: readonly string[] | undefined | null, required: readonly string[]): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  const owned = new Set(userRoles.map(normalise));
  return required.some((r) => owned.has(normalise(r)));
}

/** True for internal LeftShield staff roles (System.Admin / Organization.Admin) — NOT the customer's own admins. */
export function isStaff(userRoles: readonly string[] | undefined | null): boolean {
  return hasAnyRole(userRoles, STAFF_ROLES);
}

/** True for any admin-shaped role (tenant admin, org admin, system admin). */
export function isAdmin(userRoles: readonly string[] | undefined | null): boolean {
  return hasAnyRole(userRoles, ADMIN_ROLES);
}
